// The one list of the games and of their variants for everything that counts wins (owner 2026-10-31: "to musi być
// modułowe, bo pasjansów będzie dochodzić, kształtów Mahjonga możliwe, że też"). A new game needs only its id in
// GAME_IDS (engine/types.ts) and, when its deals come in variants, one entry in GAME_VARIANTS here. Read by the
// ranking filters (windows/StatsPanel.tsx), the Dashboard's Details tab (windows/DetailsPanel.tsx) and, bundled into
// server/shared/games.js (scripts/build-server-rules.mjs, entry online/server-games.ts), by the server's ranking and
// details queries (server/routes/stats.js, server/lib/playerDetails.js).
import { LENGTHS } from '../358/rules';
import { TARGETS } from '../1000/rules';
import { LINES } from '../kulki/rules';
import { MAHJONG_LAYOUTS } from './rules/mahjong';
import { GAME_IDS, type GameId } from './types';

/** The games that count wins, in the order the Dashboard's chart and the popularity count list them. */
export const GAMES: readonly string[] = GAME_IDS;

/**
 * A game whose deals come in variants the ranking can filter by (Mahjong's shape, 3-5-8's length, Tysiąc's
 * target). The variant is stored at the end of the win's shuffle pattern, one number after the cards or tiles.
 */
export interface GameVariant {
  /** The request parameter of the filter and its key in the Filters (StatsPanel.tsx). */
  readonly param: 'layout_type' | 'length_type' | 'target_type' | 'line_type';
  /** Where the variant sits in the stored pattern: 1-based, the argument of split_part. */
  readonly at: number;
  /** What the pattern stores: the index into `values` (Mahjong stores the layout's number) or the value itself. */
  readonly stored: 'index' | 'value';
  /** The variants, as the filter and the texts name them. */
  readonly values: readonly string[];
  /** The field of a ranking row that carries the variant (kept apart for the old `layout` of Mahjong). */
  readonly field: 'layout' | 'variant';
  /** The filter's value for "every variant". */
  readonly all: string;
  /** Text keys: the filter's label, the "every variant" word, a variant's name. */
  readonly labelKey: string;
  readonly allKey: string;
  readonly nameKey: (value: string) => string;
}

export const GAME_VARIANTS: Partial<Record<GameId, GameVariant>> = {
  // the layout's number after the 144 tiles (owner 2026-10-27)
  mahjong: {
    param: 'layout_type',
    at: 145,
    stored: 'index',
    values: MAHJONG_LAYOUTS.map((l) => l.name),
    field: 'layout',
    all: 'all-layouts',
    labelKey: 'mahjong.layout.label',
    allKey: 'mahjong.layout.all',
    nameKey: (v) => `mahjong.layout.${v}`,
  },
  // the number of deals after the 52 cards (owner 2026-10-28)
  'three-five-eight': {
    param: 'length_type',
    at: 53,
    stored: 'value',
    values: LENGTHS.map(String),
    field: 'variant',
    all: 'all',
    labelKey: 't58.length.label',
    allKey: 't58.length.all',
    nameKey: (v) => `t58.length.${v}`,
  },
  // the points to play to, after the 52 cards (owner 2026-10-28)
  thousand: {
    param: 'target_type',
    at: 53,
    stored: 'value',
    values: TARGETS.map(String),
    field: 'variant',
    all: 'all',
    labelKey: 't1k.target.label',
    allKey: 't1k.target.all',
    nameKey: (v) => `t1k.target.${v}`,
  },
  // the shortest line that leaves, after the 52 numbers (owner 2026-11-03: 5 in a row, or 4 in the easier version)
  kulki: {
    param: 'line_type',
    at: 53,
    stored: 'value',
    values: LINES.map(String),
    field: 'variant',
    all: 'all',
    labelKey: 'kulki.line.label',
    allKey: 'kulki.line.all',
    nameKey: (v) => `kulki.line.${v}`,
  },
};

export const variantOf = (game: string): GameVariant | undefined => GAME_VARIANTS[game as GameId];

/** The variant a stored pattern number means, or undefined when it is none of the game's. */
export function variantValue(v: GameVariant, raw: string): string | undefined {
  if (!/^\d+$/.test(raw)) return undefined;
  if (v.stored === 'index') return v.values[Number(raw)];
  return v.values.includes(raw) ? raw : undefined;
}

/** What the pattern stores for a filter's pick, or null when the pick is not one of the variants. */
export function variantStored(v: GameVariant, picked: string): string | null {
  if (!v.values.includes(picked)) return null;
  return v.stored === 'index' ? String(v.values.indexOf(picked)) : picked;
}
